import { useEffect, useRef } from 'react';
import { prefersReducedMotion, watchReducedMotion } from '../motion/reducedMotion';

const VERTEX_SHADER = `
attribute vec2 aPosition;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
}
`;

// Volumetric brand-coloured curtains: three stacked fbm fields drifting at different rates,
// composited premultiplied so the page background shows through the thin parts.
const FRAGMENT_SHADER = `
precision mediump float;

uniform vec2 uResolution;
uniform float uTime;
uniform vec2 uPointer;
uniform float uIntensity;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
    u.y
  );
}

float fbm(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;
  for (int i = 0; i < 4; i++) {
    value += amplitude * noise(p);
    p *= 2.03;
    amplitude *= 0.5;
  }
  return value;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;
  vec2 p = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;

  float t = uTime * 0.05;
  vec2 flow = vec2(t * 0.6, -t * 0.35) + uPointer * 0.1;

  float base = fbm(p * 1.6 + flow);
  float ridge = fbm(p * 2.9 - flow * 1.4 + base * 0.9);
  float veil = fbm(p * 5.2 + vec2(-t, t) * 0.8 + ridge * 0.5);

  float curtain = smoothstep(0.32, 0.92, ridge);
  float glow = smoothstep(0.55, 1.0, base);
  float dust = smoothstep(0.72, 1.0, veil);

  vec3 cyan = vec3(0.075, 0.847, 0.902);
  vec3 violet = vec3(0.663, 0.565, 1.0);
  vec3 gold = vec3(0.937, 0.773, 0.416);

  vec3 color = mix(cyan, violet, smoothstep(0.15, 0.85, base));
  color = mix(color, gold, dust * 0.55);

  float vignette = smoothstep(1.2, 0.1, length(p * vec2(0.85, 1.15)));
  float sky = smoothstep(-0.1, 0.9, 1.0 - uv.y);
  float alpha = (curtain * 0.42 + glow * 0.3 + dust * 0.11) * vignette * mix(0.4, 1.0, sky);
  alpha = clamp(alpha, 0.0, 0.6) * uIntensity;

  gl_FragColor = vec4(color * alpha, alpha);
}
`;

function compile(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
}

type AuroraFieldProps = {
  /** Multiplies the final opacity so one scene can sit louder than another. */
  intensity?: number;
  className?: string;
};

export function AuroraField({ intensity = 1, className = '' }: AuroraFieldProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return undefined;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: false, depth: false, powerPreference: 'low-power' });
    if (!gl) {
      host.dataset.mode = 'fallback';
      return undefined;
    }

    const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!vertex || !fragment || !program) {
      host.dataset.mode = 'fallback';
      return undefined;
    }
    gl.attachShader(program, vertex);
    gl.attachShader(program, fragment);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      host.dataset.mode = 'fallback';
      return undefined;
    }

    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const uResolution = gl.getUniformLocation(program, 'uResolution');
    const uTime = gl.getUniformLocation(program, 'uTime');
    const uPointer = gl.getUniformLocation(program, 'uPointer');
    const uIntensity = gl.getUniformLocation(program, 'uIntensity');

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.uniform1f(uIntensity, intensity);

    host.dataset.mode = 'webgl';

    // The field is soft by nature, so rendering under-size and letting the browser upscale
    // buys a large fill-rate saving that nobody can see.
    const RENDER_SCALE = 0.7;
    let width = 0;
    let height = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const rect = host.getBoundingClientRect();
      const next = {
        w: Math.max(1, Math.round(rect.width * dpr * RENDER_SCALE)),
        h: Math.max(1, Math.round(rect.height * dpr * RENDER_SCALE)),
      };
      if (next.w === width && next.h === height) return;
      width = next.w;
      height = next.h;
      canvas.width = width;
      canvas.height = height;
      gl.viewport(0, 0, width, height);
      gl.uniform2f(uResolution, width, height);
    };

    let frame = 0;
    let start = performance.now();
    let elapsed = 0;
    let pointerX = 0;
    let pointerY = 0;
    let targetX = 0;
    let targetY = 0;
    let visible = true;
    let running = false;
    let reduced = prefersReducedMotion();

    const draw = (time: number) => {
      pointerX += (targetX - pointerX) * 0.05;
      pointerY += (targetY - pointerY) * 0.05;
      gl.uniform1f(uTime, time);
      gl.uniform2f(uPointer, pointerX, pointerY);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const loop = (now: number) => {
      elapsed = (now - start) / 1000;
      draw(elapsed);
      frame = requestAnimationFrame(loop);
    };

    const play = () => {
      if (running || reduced || !visible || document.hidden) return;
      running = true;
      start = performance.now() - elapsed * 1000;
      frame = requestAnimationFrame(loop);
    };

    const pause = () => {
      running = false;
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };

    const handlePointer = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      targetX = (event.clientX / window.innerWidth - 0.5) * 2;
      targetY = (event.clientY / window.innerHeight - 0.5) * 2;
    };

    const handleVisibility = () => (document.hidden ? pause() : play());

    const handleContextLost = (event: Event) => {
      event.preventDefault();
      pause();
      host.dataset.mode = 'fallback';
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) play();
        else pause();
      },
      { threshold: 0 },
    );
    observer.observe(host);

    const resizeObserver = new ResizeObserver(() => {
      resize();
      if (!running) draw(elapsed);
    });
    resizeObserver.observe(host);

    const unwatch = watchReducedMotion((next) => {
      reduced = next;
      if (reduced) {
        pause();
        draw(0);
      } else {
        play();
      }
    });

    window.addEventListener('pointermove', handlePointer, { passive: true });
    document.addEventListener('visibilitychange', handleVisibility);
    canvas.addEventListener('webglcontextlost', handleContextLost);

    resize();
    if (reduced) draw(0);
    else play();

    return () => {
      pause();
      unwatch();
      observer.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener('pointermove', handlePointer);
      document.removeEventListener('visibilitychange', handleVisibility);
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
    };
  }, [intensity]);

  return (
    <div ref={hostRef} className={`aurora ${className}`} data-mode="idle" aria-hidden="true">
      <canvas ref={canvasRef} className="aurora__canvas" />
    </div>
  );
}
